import { useCallback, useEffect, useState } from 'react';
import { Category } from '@/class/Category';
import { HTTPResponse } from '@/class/HTTPResponse';
import { Item } from '@/class/Item';
import { Option } from '@/class/Option';
import { fetchData, putData } from '../../../lib/api';
import Select from '../../../utils/select';
import ItemModal from './create';
import React from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import {
  adminCard2,
  adminInteractive,
  adminInput,
  adminTableHead,
  adminTableCell,
  adminButtonPrimary,
  adminTableWrap,
  adminTable,
  adminTableCellName,
} from '../../layout/adminStyles';

const buildItemPricePayload = (
  item: Item,
  overrides: Partial<{
    price: number;
    price_excluding_tax: number | null;
    unit: string;
    comment: string | null;
  }>
) => ({
  price: overrides.price ?? item.price,
  unit: overrides.unit ?? item.unit ?? '',
  comment: overrides.comment !== undefined ? overrides.comment : (item.comment ?? null),
  ...(overrides.price_excluding_tax !== undefined
    ? { price_excluding_tax: overrides.price_excluding_tax }
    : {}),
});

const TAX_RATE = 1.08;

const toTaxIncluded = (taxExcluded: number) => Math.floor(taxExcluded * TAX_RATE);

type PriceEditField = 'excluded' | 'included';

const AdminItemList = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [selectedSort, setSelectedSort] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [editingItemId, setEditingItemId] = useState<number>();
  const [editingPriceField, setEditingPriceField] = useState<PriceEditField>();
  const [editedTaxExcluded, setEditedTaxExcluded] = useState<number>();
  const [editedTaxIncluded, setEditedTaxIncluded] = useState<number>();
  const [editingUnitItemId, setEditingUnitItemId] = useState<number>();
  const [editedUnit, setEditedUnit] = useState<string>('');
  const [editingCommentItemId, setEditingCommentItemId] = useState<number>();
  const [editedComment, setEditedComment] = useState<string>('');
  const [checkedItemIds, setCheckedItemIds] = useState<number[]>([]);
  const [allItemChecked, setAllItemChecked] = useState<boolean>(false);
  const [specialSaleItemId, setSpecialSaleItemId] = useState<number>();
  const [categoryOption, setCategoryOption] = useState<Option[]>([]);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const [isTimeSaleAnnounced, setIsTimeSaleAnnounced] = useState<boolean>(false);
  const [isUpdatingTimeSaleAnnouncement, setIsUpdatingTimeSaleAnnouncement] = useState<boolean>(false);
  const { store, updateStore } = useAuthContext();

  const clearPriceEditing = () => {
    setEditingItemId(undefined);
    setEditingPriceField(undefined);
    setEditedTaxExcluded(undefined);
    setEditedTaxIncluded(undefined);
  };

  const startPriceEditing = (
    itemId: number,
    currentTaxIncluded: number,
    field: PriceEditField,
    currentTaxExcluded?: number | null
  ) => {
    setEditingItemId(itemId);
    setEditingPriceField(field);
    setEditedTaxIncluded(Number(currentTaxIncluded));
    // 税込から税抜への逆算は行わない（保存済みの税抜のみ初期表示）
    setEditedTaxExcluded(
      field === 'excluded' && currentTaxExcluded != null ? Number(currentTaxExcluded) : undefined
    );
  };

  const handleTaxExcludedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '') {
      setEditedTaxExcluded(undefined);
      return;
    }

    const taxExcluded = Number(value);
    setEditedTaxExcluded(taxExcluded);
    if (!Number.isNaN(taxExcluded)) {
      setEditedTaxIncluded(toTaxIncluded(taxExcluded));
    }
  };

  const handleTaxIncludedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEditedTaxIncluded(value === '' ? undefined : Number(value));
  };

  const handlePriceBlur = async (itemId: number) => {
    const target = items.find((item: Item) => item.item_id === itemId);
    if (!target || editedTaxIncluded == null || Number.isNaN(editedTaxIncluded)) {
      clearPriceEditing();
      return;
    }

    const nextPriceExcludingTax =
      editingPriceField === 'excluded'
        ? editedTaxExcluded != null && !Number.isNaN(editedTaxExcluded)
          ? editedTaxExcluded
          : null
        : undefined;

    const priceUnchanged = editedTaxIncluded === Number(target.price);
    const excludingTaxUnchanged =
      nextPriceExcludingTax === undefined ||
      nextPriceExcludingTax === (target.price_excluding_tax ?? null);

    if (priceUnchanged && excludingTaxUnchanged) {
      clearPriceEditing();
      return;
    }

    try {
      const res: HTTPResponse = await putData(
        `item_price/${itemId}/${store.id}`,
        buildItemPricePayload(target, {
          price: editedTaxIncluded,
          ...(nextPriceExcludingTax !== undefined
            ? { price_excluding_tax: nextPriceExcludingTax }
            : {}),
        })
      );

      if (res.data?.price != null) {
        const updatedItems = items.map((item: Item) => {
          if (item.item_id === itemId) {
            return {
              ...item,
              price: editedTaxIncluded,
              ...(nextPriceExcludingTax !== undefined
                ? { price_excluding_tax: nextPriceExcludingTax }
                : {}),
            };
          }
          return item;
        });
        setItems(updatedItems as Item[]);
        clearPriceEditing();
      }
    } catch {
      console.log('価格の更新に失敗ました。');
    }
  };

  const sortOption: Option[] = [
    { id: 'name', name: '名前' },
    { id: 'price', name: '金額' },
  ];

  const unitOption: Option[] = [
    { id: '1パック', name: '1パック' },
    { id: '100g', name: '100g' },
    { id: '柵', name: '柵' },
    { id: '袋', name: '袋' },
    { id: '玉', name: '玉' },
    { id: '束', name: '束' },
    { id: '本', name: '本' },
    { id: '個', name: '個' },
    { id: '1/2', name: '1/2' },
    { id: '1/4', name: '1/4' },
    { id: 'ネット', name: 'ネット' },
    { id: '尾', name: '尾' },
    { id: '匹', name: '匹' },
    { id: 'ダース', name: 'ダース' },
    { id: '人前', name: '人前' },
  ];

  const handleUnitClick = (itemId: number, currentUnit: string) => {
    setEditingUnitItemId(itemId);
    setEditedUnit(currentUnit ?? '');
  };

  const handleUnitChange = async (itemId: number, value: string) => {
    setEditedUnit(value);

    const target = items.find((item: Item) => item.item_id === itemId);

    try {
      const res: HTTPResponse = await putData(`item_price/${itemId}/${store.id}`, buildItemPricePayload(target!, {
        unit: value,
      }));

      if (res.data?.unit) {
        const updatedItems = items.map((item: Item) => {
          if (item.item_id === itemId) {
            return { ...item, unit: value };
          }
          return item;
        });
        setItems(updatedItems as Item[]);
        setEditingUnitItemId(undefined);
      }
    } catch {
      console.log('単位の更新に失敗ました。');
    }
  };

  const handleCommentClick = (itemId: number, currentComment: string) => {
    setEditingCommentItemId(itemId);
    setEditedComment(currentComment ?? '');
  };

  const handleCommentBlur = async (itemId: number) => {
    const target = items.find((item: Item) => item.item_id === itemId);
    if (!target) {
      return;
    }

    const nextComment = editedComment.trim() || null;
    if ((target.comment ?? null) === nextComment) {
      setEditingCommentItemId(undefined);
      return;
    }

    try {
      const res: HTTPResponse = await putData(`item_price/${itemId}/${store.id}`, buildItemPricePayload(target, {
        comment: nextComment,
      }));

      if (res.data) {
        const updatedItems = items.map((item: Item) =>
          item.item_id === itemId ? { ...item, comment: nextComment } : item
        );
        setItems(updatedItems as Item[]);
        setEditingCommentItemId(undefined);
      }
    } catch {
      console.log('コメントの更新に失敗しました。');
    }
  };

  const renderCommentField = (item: Item, options?: { compact?: boolean }) => {
    const emptyLabel = options?.compact ? '—' : 'クリックしてコメントを入力';

    if (editingCommentItemId === item.item_id) {
      return (
        <input
          type="text"
          className={`${adminInput} w-full py-1`}
          value={editedComment}
          maxLength={255}
          placeholder="例: 本日入荷"
          onChange={(e) => setEditedComment(e.target.value)}
          onBlur={() => handleCommentBlur(item.item_id)}
          autoFocus
        />
      );
    }

    return (
      <span
        className={`${adminInteractive} block w-full text-sm text-stone-700 ${options?.compact ? 'truncate max-w-[12rem]' : ''}`}
        onClick={() => handleCommentClick(item.item_id, item.comment ?? '')}
        title={item.comment ?? 'コメントを入力'}
      >
        {item.comment || emptyLabel}
      </span>
    );
  };

  const filteredItems = selectedCategory
    ? items.filter((item: Item) => String(item.category_id) === selectedCategory)
    : items;

  const loadCategories = useCallback(async () => {
    try {
      const res: HTTPResponse = await fetchData('category');
      const categories = Array.isArray(res.data) ? res.data : [];
      setCategoryOption(
        categories.map((category: Category) => ({
          id: String(category.id),
          name: category.name,
        }))
      );
    } catch (err: unknown) {
      console.error(err);
      setCategoryOption([]);
    }
  }, []);

  const loadItems = useCallback(async () => {
    if (!store?.id) {
      setItems([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const res: HTTPResponse = await fetchData(
        `item_price?store_id=${store.id}&sort_key[]=furigana`
      );
      const data = Array.isArray(res.data) ? res.data : [];
      setItems(data);
      const specialItem = data.find((item: Item) => item.value_status === 1);
      setSpecialSaleItemId(specialItem?.item_id);
    } catch (err: any) {
      console.error(err.message);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [store?.id]);

  useEffect(() => {
    loadCategories();
    loadItems();
  }, [loadCategories, loadItems]);

  useEffect(() => {
    setIsTimeSaleAnnounced(Boolean(store?.is_time_sale_announced));
  }, [store?.id, store?.is_time_sale_announced]);

  const openModal = (item?: Item): void => {
    setIsShowModal(true);
  };

  const closeModal = () => {
    setIsShowModal(false);
  };

  const handleCheckboxChange = useCallback((itemId: number, isChecked: boolean) => {
    setCheckedItemIds((prevCheckedItems: number[]) => {
      if (isChecked) {
        return [...prevCheckedItems, itemId];
      }
      return prevCheckedItems.filter((id) => id !== itemId);
    });
  }, []);

  const handleCheckAll = useCallback(
    (isChecked: boolean) => {
      setAllItemChecked(isChecked);
      if (isChecked) {
        const allItemIds = items.map((item: Item) => item.item_id);
        setCheckedItemIds(allItemIds);
      } else {
        setCheckedItemIds([]);
      }
    },
    [items]
  );

  const isItemChecked = useCallback(
    (itemId: number) => {
      return checkedItemIds.includes(itemId);
    },
    [checkedItemIds]
  );

  const handleSpecialSaleChange = async (itemId: number) => {
    if (!store?.id) {
      return;
    }

    try {
      const res: HTTPResponse = await putData(
        `item_price/${itemId}/${store.id}/value_status`,
        {}
      );

      if (res.data?.value_status === 1) {
        setSpecialSaleItemId(itemId);
        setItems((prevItems) =>
          prevItems.map((item: Item) => ({
            ...item,
            value_status: item.item_id === itemId ? 1 : 0,
          }))
        );
        alert('特売商品になりました');
      }
    } catch {
      console.log('特売の更新に失敗しました。');
    }
  };

  const allItemCheckedClass = checkedItemIds.length === 0 ? 'disabled:bg-gray-400' : '';

  const handleComplete = async () => {
    if (!store?.id) {
      return;
    }

    if (editingItemId != null) {
      await handlePriceBlur(editingItemId);
    }
    setEditingUnitItemId(undefined);

    setIsCompleting(true);
    try {
      const res: HTTPResponse = await putData(`store/${store.id}/daily-completion`, {});

      if (res.data) {
        alert('本日の商品情報の更新を完了しました。');
      }
    } catch (err: unknown) {
      console.error(err);
      alert('完了の登録に失敗しました。');
    } finally {
      setIsCompleting(false);
    }
  };

  const handleTimeSaleAnnouncementChange = async (enabled: boolean) => {
    if (!store?.id || isUpdatingTimeSaleAnnouncement) {
      return;
    }

    const previousValue = isTimeSaleAnnounced;
    setIsTimeSaleAnnounced(enabled);
    setIsUpdatingTimeSaleAnnouncement(true);

    try {
      const res: HTTPResponse = await putData(`store/${store.id}/time-sale-announcement`, {
        is_time_sale_announced: enabled,
      });

      if (res.data) {
        updateStore({
          ...store,
          is_time_sale_announced: Boolean(res.data.is_time_sale_announced),
        });
      }
    } catch (err: unknown) {
      console.error(err);
      setIsTimeSaleAnnounced(previousValue);
      alert('タイムセール告知の更新に失敗しました。');
    } finally {
      setIsUpdatingTimeSaleAnnouncement(false);
    }
  };

  return (
    <>
      <div className={adminCard2}>
        <div className="flex items-center mb-4 hidden">
          <label className="inline-flex items-center text-gray-700 mr-4">
            <input
              type="checkbox"
              className="form-checkbox h-5 w-5 text-green-600"
              checked={allItemChecked}
              onChange={(e) => handleCheckAll(e.target.checked)}
            />
            <span className="ml-2">全て選択</span>
          </label>
          <button
            className={`bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline hidden ${allItemCheckedClass} disabled:cursor-not-allowed`}
            disabled
          >
            選択した商品を削除
          </button>
        </div>

        <div className="mt-4 mb-4">
          <div className="flex flex-row items-center justify-between gap-3">
            <label htmlFor="category" className="text-sm font-medium text-stone-700">
              商品カテゴリ:
            </label>
            <button
              type="button"
              className="inline-flex shrink-0 items-center justify-center rounded-lg bg-teal-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-teal-600 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={handleComplete}
              disabled={!store?.id || isCompleting}
            >
              {isCompleting ? '更新中...' : '本日の価格を公開する'}
            </button>
          </div>
          <div className="mt-2 w-full max-w-[240px]">
            <Select
              options={categoryOption}
              value={selectedCategory}
              cssClass={adminInput + ' w-full'}
              onChange={(value) => setSelectedCategory(value)}
            />
          </div>
        </div>

        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <span className="text-sm font-medium text-stone-700">タイムセール告知</span>
          <div className="flex flex-wrap items-center gap-4">
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-stone-700">
              <input
                type="radio"
                name="time-sale-announcement"
                className="h-4 w-4 text-blue-500 focus:ring-blue-300"
                checked={isTimeSaleAnnounced}
                onChange={() => handleTimeSaleAnnouncementChange(true)}
                disabled={!store?.id || isUpdatingTimeSaleAnnouncement}
              />
              している
            </label>
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-stone-700">
              <input
                type="radio"
                name="time-sale-announcement"
                className="h-4 w-4 text-blue-500 focus:ring-blue-300"
                checked={!isTimeSaleAnnounced}
                onChange={() => handleTimeSaleAnnouncementChange(false)}
                disabled={!store?.id || isUpdatingTimeSaleAnnouncement}
              />
              していない
            </label>
          </div>
        </div>
        <div className="flex items-center mb-4 hidden">
          <label htmlFor="search-term" className="text-gray-700 mr-2">
            検索:
          </label>
          <input
            type="text"
            id="search-term"
            placeholder="商品名で検索"
            className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mr-4"
          />
          <label htmlFor="sort-by" className="text-gray-700 mr-2">
            並び替え:
          </label>
          <Select
            options={sortOption}
            value={selectedSort}
            cssClass="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mr-4"
            onChange={(value) => setSelectedSort(value)}
          />
          <button
            type="button"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            適用
          </button>
        </div>

        <div className={`${adminTableWrap} max-h-[min(55vh,28rem)] overflow-y-auto`}>
          <table className={adminTable}>
            <thead className="sticky top-0 z-10 bg-stone-50">
              <tr className={adminTableHead}>
                <th className={`${adminTableCell} text-left hidden`}></th>
                <th className={`${adminTableCell} text-left hidden`}>写真</th>
                <th className={`${adminTableCell} text-center w-12`}>特売</th>
                <th className={`${adminTableCellName} text-left text-xs sm:text-sm w-[36%] sm:w-[40%]`}>商品名</th>
                <th className={`${adminTableCell} text-left text-xs sm:text-sm w-12 sm:w-14`}>単位</th>
                <th className={`${adminTableCell} text-left text-xs sm:text-sm w-16 sm:w-20`}>（税抜）</th>
                <th className={`${adminTableCell} text-left text-xs sm:text-sm w-16 sm:w-20`}>（税込）</th>
                <th className={`${adminTableCell} text-left hidden md:table-cell min-w-[10rem]`}>コメント</th>
                <th className={`${adminTableCell} text-left hidden`}>説明</th>
                <th className={`${adminTableCell} text-center hidden`}>編集</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className={`${adminTableCell} p-6 text-center text-stone-500`}>
                    読み込み中...
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className={`${adminTableCell} p-6 text-center text-stone-500`}>
                    {items.length === 0
                      ? '商品がありません。ログイン情報またはデータをご確認ください。'
                      : '該当する商品がありません。'}
                  </td>
                </tr>
              ) : (
                filteredItems.map((item: Item) => (
                  <React.Fragment key={`item-${item.item_id}`}>
                    <tr className="md:border-b md:border-stone-200">
                      <td className={`${adminTableCell} hidden`}>
                        <input
                          type="checkbox"
                          className="form-checkbox h-4 w-4 text-teal-500"
                          checked={isItemChecked(item.item_id)}
                          onChange={(e) => handleCheckboxChange(item.item_id, e.target.checked)}
                        />
                      </td>
                      <td className={`${adminTableCell} hidden`}>
                        <img
                          src="placeholder.png"
                          alt="商品1"
                          className="max-w-20 h-auto block mx-auto"
                        />
                      </td>
                      <td className={`${adminTableCell} text-center align-top`}>
                        <input
                          type="radio"
                          name="special-sale-item"
                          className="h-4 w-4 text-teal-500 focus:ring-teal-300"
                          checked={specialSaleItemId === item.item_id}
                          onChange={() => handleSpecialSaleChange(item.item_id)}
                          aria-label={`${item.item_name}を特売にする`}
                        />
                      </td>
                      <td className={`${adminTableCellName} align-top text-xs sm:text-sm w-[36%] sm:w-[40%]`} title={item.item_name}>
                        {item.item_name}
                      </td>
                      <td className={`${adminTableCell} w-12 sm:w-14 align-top text-xs sm:text-sm`}>
                        {editingUnitItemId === item.item_id ? (
                          <Select
                            options={unitOption}
                            value={editedUnit}
                            cssClass={adminInput + ' py-1 w-full min-w-0 text-xs sm:text-sm'}
                            onChange={(value) => handleUnitChange(item.item_id, value)}
                          />
                        ) : (
                          <span
                            className={`${adminInteractive} text-xs sm:text-sm`}
                            onClick={() => handleUnitClick(item.item_id, item.unit ?? '')}
                          >
                            {item.unit}
                          </span>
                        )}
                      </td>
                      <td className={`${adminTableCell} text-right align-top text-xs sm:text-sm w-16 sm:w-20`}>
                        {editingItemId === item.item_id && editingPriceField === 'excluded' ? (
                          <input
                            type="number"
                            className={`${adminInput} w-16 sm:w-20 py-1 text-center text-xs sm:text-sm`}
                            value={editedTaxExcluded ?? ''}
                            onChange={handleTaxExcludedChange}
                            onBlur={() => handlePriceBlur(item.item_id)}
                            autoFocus
                          />
                        ) : (
                          <span
                            className={`${adminInteractive} text-xs sm:text-sm`}
                            onClick={() =>
                              startPriceEditing(
                                item.item_id,
                                item.price,
                                'excluded',
                                item.price_excluding_tax
                              )
                            }
                          >
                            {item.price_excluding_tax != null
                              ? `¥${item.price_excluding_tax}`
                              : '—'}
                          </span>
                        )}
                      </td>
                      <td className={`${adminTableCell} text-right align-top text-xs sm:text-sm w-16 sm:w-20`}>
                        {editingItemId === item.item_id && editingPriceField === 'included' ? (
                          <input
                            type="number"
                            className={`${adminInput} w-16 sm:w-20 py-1 text-center text-xs sm:text-sm`}
                            value={editedTaxIncluded ?? ''}
                            onChange={handleTaxIncludedChange}
                            onBlur={() => handlePriceBlur(item.item_id)}
                            autoFocus
                          />
                        ) : (
                          <span
                            className={`${adminInteractive} text-xs sm:text-sm`}
                            onClick={() =>
                              startPriceEditing(
                                item.item_id,
                                item.price,
                                'included',
                                item.price_excluding_tax
                              )
                            }
                          >
                            ¥
                            {editingItemId === item.item_id && editedTaxIncluded != null && !Number.isNaN(editedTaxIncluded)
                              ? editedTaxIncluded
                              : item.price}
                          </span>
                        )}
                      </td>
                      <td className={`${adminTableCell} hidden md:table-cell align-top`}>
                        {renderCommentField(item, { compact: true })}
                      </td>
                      <td className={`${adminTableCell} hidden`}>説明</td>
                      <td className={`${adminTableCell} text-center hidden`}>
                        <button
                          className={`${adminButtonPrimary} text-sm py-1 px-3`}
                          onClick={() => openModal(item)}
                        >
                          編集
                        </button>
                      </td>
                    </tr>
                    <tr className="border-b border-stone-200 md:hidden">
                      <td className={`${adminTableCell} hidden`}></td>
                      <td className={`${adminTableCell} hidden`}></td>
                      <td colSpan={5} className={`${adminTableCell} pt-0 pb-3 bg-stone-50/40`}>
                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-stone-500 shrink-0">コメント</span>
                          {renderCommentField(item)}
                        </div>
                      </td>
                      <td className={`${adminTableCell} hidden`}></td>
                      <td className={`${adminTableCell} hidden`}></td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <ItemModal isShowModal={isShowModal} closeModal={closeModal} />
    </>
  );
};

export default AdminItemList;
